import React, { FC } from 'react';
import { IconChevron } from '../../utils/icons';

const BTN =
    'inline-flex items-center gap-[5px] px-5 py-[11px] rounded-[10px] text-[14px] font-semibold cursor-pointer border border-transparent transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
const BTN_GHOST = `${BTN} bg-surface-2 text-text-2 enabled:hover:bg-track enabled:hover:text-text-1`;
// The darker gold hover has no token (--accent-dark is lighter), and the gold
// family is constant across themes, so it stays literal.
const BTN_GOLD = `${BTN} bg-accent text-white enabled:hover:bg-[#967435] max-[560px]:flex-1 max-[560px]:justify-center`;

interface Props {
    progress: number;
    saving: boolean;
    canGoBack: boolean;
    isLastStep: boolean;
    alreadySubmitted: boolean;
    onSave: () => void;
    onBack: () => void;
    onNext: () => void;
    onSubmit: () => void;
    readOnly?: boolean;
}

export const FooterBar: FC<Props> = ({
    progress,
    saving,
    canGoBack,
    isLastStep,
    alreadySubmitted,
    onSave,
    onBack,
    onNext,
    onSubmit,
    readOnly,
}) => (
    // The 95% bar fill stays literal: an opacity modifier cannot tint a
    // var()-backed color, so bg-surface/95 would emit no background at all.
    <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-[8px] border-t border-border-token z-50">
        <div className="max-w-[680px] mx-auto px-5 py-[13px] flex items-center justify-between gap-[14px] max-[560px]:flex-wrap">
            <div className="flex items-center gap-2.5 flex-1 min-w-0 max-[560px]:order-2 max-[560px]:w-full">
                <div className="flex-1 max-w-[160px] h-1.5 bg-track rounded-[3px] overflow-hidden">
                    {/* Two-stop gold gradient: no token pair expresses it, and
                        the gold family is constant across themes. */}
                    <div
                        className="h-full bg-gradient-to-r from-[#C8A97E] to-[#DFC9A3] rounded-[3px] transition-[width] duration-[400ms] ease-out"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <span className="text-[12px] text-text-2 font-semibold whitespace-nowrap">
                    {readOnly ? 'Read only' : `${progress}% complete`}
                </span>
            </div>
            <div className="flex gap-2 max-[560px]:order-1 max-[560px]:w-full">
                {!readOnly && (
                    <button
                        type="button"
                        className={BTN_GHOST}
                        onClick={onSave}
                        disabled={saving}
                    >
                        Save for Later
                    </button>
                )}
                {canGoBack && (
                    <button type="button" className={BTN_GHOST} onClick={onBack}>
                        <IconChevron dir="L" size={15} />
                        Back
                    </button>
                )}
                {isLastStep && !readOnly ? (
                    <button
                        type="button"
                        className={BTN_GOLD}
                        onClick={onSubmit}
                        disabled={saving}
                    >
                        {(() => {
                            if (saving) return 'Saving…';
                            return alreadySubmitted
                                ? 'Update Feedback'
                                : 'Submit Feedback';
                        })()}
                    </button>
                ) : (
                    !isLastStep && (
                        <button type="button" className={BTN_GOLD} onClick={onNext}>
                            Next
                            <IconChevron dir="R" size={15} stroke="#FFF" />
                        </button>
                    )
                )}
            </div>
        </div>
    </div>
);
